import React, { useState, useEffect, useRef } from "react";
import {
  Handle,
  Position,
  NodeProps,
  useReactFlow,
  useEdges,
  useNodes,
} from "reactflow";
import {
  FlaskConical,
  Code,
  Maximize2,
  Moon,
  Sun,
  Info,
  CircleX,
} from "lucide-react";
import { Button } from "@components/ui/button";
import { Switch } from "@components/ui/switch";
import Confirmation from "@components/common/ConfirmDialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { toast } from "@components/ui/use-toast";
import { getConnectedInputs, getExecutionPath } from "../common/nodeDataFlow";
import { ValidatedTitleInput } from "../components/ValidatedTitleInput";
import { useResizablePanels } from "../hooks/useResizablePanels";
import { PanelDivider } from "../common/PanelDivider";
import { executeWorkflow } from "../createPluginApis";
import PaginatedInputViewer from "../common/PaginatedInputViewer";
import ResponseViewer from "../common/ResponseViewer";
import NodeContextMenu, {
  useNodeContextMenu,
  createContextMenuItems,
} from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";
import NodeContentSection from "../common/NodeContentSection";
import {
  getNodeExecutionParameters,
  buildWorkflowPayload,
  handleNodeExecutionResults,
  findDownstreamNodes,
} from "../utils/nodeExecutionUtils";
import { getNodeBackendType } from "../utils/nodeTransformUtils";
import { handlerStyle, unwrapJsonData } from "../common/commonData";
import CodeMirror from "@uiw/react-codemirror";
import { python, pythonLanguage } from "@codemirror/lang-python";
import { linter, Diagnostic } from "@codemirror/lint";
import { CompletionContext, Completion } from "@codemirror/autocomplete";
import { tooltips } from "@codemirror/view";
import { oneDark } from "@codemirror/theme-one-dark";
import { FullscreenWrapper } from "../common/withFullscreen";
import { FullscreenToggleButton } from "../common/FullscreenToggleButton";
import { getSharedTooltipHost } from "../common/sharedTooltipHost.ts";

interface MeshHint {
  label: string;
  type: string;
  detail: string;
  desc: string;
  example: string;
  category: string;
  color: string;
  apply?: string;
}

const MESH_HINTS: MeshHint[] = [
  {
    label: "result",
    type: "variable",
    detail: "any",
    desc: "Assign your output to this variable — required for node output",
    example: 'result = {"status": "ok", "count": 5}',
    category: "Output",
    color: "#5F5E5A",
  },
  {
    label: "items",
    type: "variable",
    detail: "list",
    desc: "All input items from the previous node",
    example: '[{"json": {"name": "John", "age": 30}}, ...]',
    category: "Input data",
    color: "#378ADD",
  },
  {
    label: "item",
    type: "variable",
    detail: "dict",
    desc: "Current item being processed (runOnceForEachItem mode)",
    example: '{"json": {"name": "John", "age": 30}}',
    category: "Input data",
    color: "#378ADD",
  },
  {
    label: "nodes",
    type: "variable",
    detail: "dict",
    desc: "Access any upstream node's output by node name",
    example: 'nodes["HTTP Request"][0]["email"]',
    category: "Previous nodes data",
    color: "#7F77DD",
  },
  {
    label: "workflow",
    type: "variable",
    detail: "dict",
    desc: "Current workflow metadata",
    example: '{"id": "wf_123", "name": "My Workflow", "active": True}',
    category: "Workflow info",
    color: "#1D9E75",
  },
  {
    label: "threatlookup",
    type: "variable",
    detail: "object",
    desc: "Threat lookup data (IP, domain, hash reputation)",
    example:
      'threatlookup.get("search_value") or threatlookup.get("search_type")',
    category: "Threat intelligence",
    color: "#D85A30",
  },
  {
    label: "now",
    type: "function",
    detail: "() → str",
    desc: "Returns current datetime as ISO string",
    example: 'now()  →  "2026-06-01T10:30:45.123456"',
    category: "Date / time",
    color: "#BA7517",
    apply: "now()",
  },
  {
    label: "today",
    type: "function",
    detail: "() → str",
    desc: "Returns today's date at midnight as ISO string",
    example: 'today()  →  "2026-06-01T00:00:00"',
    category: "Date / time",
    color: "#BA7517",
    apply: "today()",
  },
];

// Builds the info panel DOM node shown beside the autocomplete popup
const buildHintInfo = (hint: MeshHint): HTMLElement => {
  const wrap = document.createElement("div");
  wrap.style.cssText =
    "padding:8px 10px;max-width:230px;font-size:12px;line-height:1.5;font-family:sans-serif";

  const cat = document.createElement("div");
  cat.style.cssText =
    `font-size:10px;font-weight:600;color:${hint.color};` +
    "margin-bottom:4px;text-transform:uppercase;letter-spacing:0.06em";
  cat.textContent = hint.category;

  const desc = document.createElement("div");
  desc.style.cssText = "color:#888;margin-bottom:7px;font-size:11px";
  desc.textContent = hint.desc;

  const exLbl = document.createElement("div");
  exLbl.style.cssText = "font-size:10px;color:#aaa;margin-bottom:3px";
  exLbl.textContent = "Example";

  const code = document.createElement("code");
  code.style.cssText =
    "display:block;font-family:monospace;font-size:11px;" +
    "background:#f0f0f0;padding:4px 7px;border-radius:4px;" +
    "color:#333;white-space:pre-wrap;word-break:break-all";
  code.textContent = hint.example;

  wrap.appendChild(cat);
  wrap.appendChild(desc);
  wrap.appendChild(exLbl);
  wrap.appendChild(code);
  return wrap;
};

// Map hints to CodeMirror Completion objects
const MESH_COMPLETIONS: Completion[] = MESH_HINTS.map((h) => ({
  label: h.label,
  type: h.type,
  detail: h.detail,
  apply: h.apply, // inserts "now()" / "today()" for functions
  boost: 99, // float to the top of the suggestion list
  info: () => buildHintInfo(h),
}));

// Completion source — triggers on any word match
const meshCompletionSource = (context: CompletionContext) => {
  const word = context.matchBefore(/\w+/);
  if (!word && !context.explicit) return null;

  const from = word?.from ?? context.pos;
  const text = (word?.text ?? "").toLowerCase();

  const options = text
    ? MESH_COMPLETIONS.filter((c) => (c.label as string).startsWith(text))
    : MESH_COMPLETIONS;

  if (options.length === 0 && !context.explicit) return null;

  return { from, options, validFor: /^\w*$/ };
};

// Register into the Python language — this merges with Python's own completions
// so keywords, builtins, and your variables all appear together
const meshflowExtension = pythonLanguage.data.of({
  autocomplete: meshCompletionSource,
});

interface PythonCodeNodeData {
  label?: string;
  header?: string;
  uid: string;
  title: string;
  description?: string;
  code?: string;
  mode?: "runOnceForAllItems" | "runOnceForEachItem";
  continueOnFail?: boolean;
  pythonEnv?: string;
  onDelete?: (id: string) => void;
  onDataChange?: (id: string, updates: any) => void;
  borderColor?: string;
  shadowColor?: string;
}

const PythonCodeNode: React.FC<NodeProps<PythonCodeNodeData>> = ({
  id,
  data,
}) => {
  const label = data.label || data.header || "Python Code";
  const [uid, setUid] = useState(data.uid || "");
  const [title, setTitle] = useState(data.title || "");
  const [description, setDescription] = useState(data.description || "");
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Context menu
  const {
    showContextMenu,
    contextMenuPosition,
    handleContextMenu,
    closeContextMenu,
  } = useNodeContextMenu(id);

  // Python code state
  const [code, setCode] = useState<string>(data.code || "");
  const [mode, setMode] = useState<"runOnceForAllItems" | "runOnceForEachItem">(
    data.mode || "runOnceForAllItems",
  );
  const [continueOnFail, setContinueOnFail] = useState(
    data.continueOnFail || false,
  );
  const pythonEnv = "python_3"; // Fixed to python_3 - no user selection allowed

  // Input/Output state
  const [inputData, setInputData] = useState<any[]>([]);
  const [outputData, setOutputData] = useState<any>(null);
  const [errorResponse, setErrorResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isCodeEditorExpanded, setIsCodeEditorExpanded] = useState(false);
  const [editorTheme, setEditorTheme] = useState<"light" | "dark">("light");
  const [showHintsDrawer, setShowHintsDrawer] = useState(false);
  const [rawOutputData, setRawOutputData] = useState<any>(null);
  const tooltipPortalExtension = React.useMemo(
    () =>
      typeof document !== "undefined"
        ? tooltips({ parent: getSharedTooltipHost(), position: "fixed" })
        : [],
    [],
  );

  const hasLogsDeep = React.useCallback((value: any): boolean => {
    if (!value) return false;

    if (Array.isArray(value)) {
      return value.some((item) => hasLogsDeep(item));
    }

    if (typeof value === "object") {
      if (Array.isArray((value as any).logs) && (value as any).logs.length > 0)
        return true;

      return Object.values(value).some((nested) => hasLogsDeep(nested));
    }

    return false;
  }, []);

  const reactFlowInstance = useReactFlow();
  const { containerRef, widths, startDrag } = useResizablePanels([
    33.33, 33.33, 33.34,
  ]);

  // Ref to track previous outputData and prevent unnecessary updates
  const prevOutputDataRef = useRef<any>(null);

  // Get edges and nodes using hooks for proper reactivity
  const edges = useEdges();
  const nodes = useNodes();

  // Copy output to clipboard
  const handleCopyOutput = () => {
    const dataToCopy = errorResponse || outputData;
    if (!dataToCopy) return;
    navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
    toast({
      variant: "success",
      title: "Copied!",
      description: errorResponse
        ? "Error copied to clipboard."
        : "Output copied to clipboard.",
      duration: 2000,
    });
  };

  // Manual refresh function for InputViewer
  const refreshInputData = React.useCallback(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [id, edges, nodes]);

  // Get input data from connected nodes
  useEffect(() => {
    const inputs = getConnectedInputs(id, edges, nodes);
    setInputData(inputs);
  }, [
    id,
    JSON.stringify(edges.filter((e) => e.target === id || e.source === id)),
    JSON.stringify(
      nodes.map((n) => ({
        id: n.id,
        configData: (n.data as any)?.configData,
        response: (n.data as any)?.response,
        outputData: (n.data as any)?.outputData,
      })),
    ),
  ]);

  // Sync inputData from data.inputData prop (set during execution)
  useEffect(() => {
    const propInputData = (data as any).inputData;

    if (
      !propInputData ||
      !Array.isArray(propInputData) ||
      propInputData.length === 0
    ) {
      return;
    }

    // Additional filtering: if propInputData contains switch format, extract items
    // This handles cases where inputData might not be properly filtered
    const filteredPropInputData = propInputData.map((input: any) => {
      // Check if this input has the switch format structure
      const hasOutputData = input.outputData || input.response;
      const isSwitchFormat =
        hasOutputData &&
        Array.isArray(hasOutputData) &&
        hasOutputData.length > 0 &&
        hasOutputData[0] !== null &&
        typeof hasOutputData[0] === "object" &&
        "rule" in hasOutputData[0] &&
        "items" in hasOutputData[0];

      if (isSwitchFormat) {
        // Switch format detected - should already be filtered by getConnectedInputs
        // but if not, extract first rule's items as fallback
        console.warn(
          `[PythonCodeNode ${id}] Switch format detected in propInputData - should be pre-filtered`,
          input,
        );
        return input;
      }

      return input;
    });

    // Only update if data actually changed
    setInputData((currentInputData) => {
      const newDataStr = JSON.stringify(filteredPropInputData);
      const currentDataStr = JSON.stringify(currentInputData);

      if (newDataStr !== currentDataStr) {
        return filteredPropInputData;
      }
      return currentInputData;
    });
  }, [JSON.stringify((data as any).inputData), id]);

  // Sync outputData from node data (updated during workflow/node execution)
  useEffect(() => {
    const newOutputData = (data as any).outputData;

    if (!newOutputData) {
      if (outputData !== null && newOutputData === null) {
        setOutputData(null);
      }
      return;
    }

    if (prevOutputDataRef.current === newOutputData) {
      return;
    }

    const newDataStr = JSON.stringify(newOutputData);
    const prevDataStr = prevOutputDataRef.current
      ? JSON.stringify(prevOutputDataRef.current)
      : null;

    if (newDataStr !== prevDataStr) {
      prevOutputDataRef.current = newOutputData;
      setOutputData(newOutputData);
    } else {
      prevOutputDataRef.current = newOutputData;
    }
  }, [(data as any).outputData]);

  // Sync error from node data (set during global workflow execution)
  useEffect(() => {
    const executionStatus = (data as any).executionStatus;
    const error = (data as any).error;

    // If node failed during global execution, show error in ResponseViewer
    if (executionStatus === "failed" && error) {
      setErrorResponse(error);
    } else if (executionStatus === "success") {
      // Clear error when node succeeds
      setErrorResponse(null);
    }
  }, [(data as any).executionStatus, (data as any).error]);

  // Sync data changes with parent
  useEffect(() => {
    if (!data.onDataChange) return;

    const storedParameters = {
      code,
      mode,
      continueOnFail,
      options: {
        python_env: pythonEnv,
      },
    };

    data.onDataChange(id, {
      uid,
      title,
      description,
      code,
      mode,
      continueOnFail,
      outputData,
      rawOutputData,
      response: outputData,
      storedParameters,
    });
  }, [
    uid,
    title,
    description,
    code,
    mode,
    continueOnFail,
    outputData,
    rawOutputData,
    id,
  ]);

  // Generate UID on mount
  useEffect(() => {
    if (!uid) setUid(Date.now().toString());
  }, []);

  // Load saved parameters from data prop (when loading from backend)
  useEffect(() => {
    const params = (data as any).storedParameters || {};

    if (params.code) setCode(params.code);
    if (params.mode) setMode(params.mode);
    if (params.continueOnFail !== undefined)
      setContinueOnFail(params.continueOnFail);
  }, []);

  useEffect(() => {
    const rawDataFromNode =
      (data as any).rawOutputData || (data as any).executionResult;

    // Prefer explicit raw/executionResult payloads because they can contain logs.
    if (rawDataFromNode) {
      setRawOutputData(rawDataFromNode);
      return;
    }

    const fallbackOutputData = (data as any).outputData;

    // Fallback only when we don't already hold logged raw data locally.
    if (fallbackOutputData && !hasLogsDeep(rawOutputData)) {
      setRawOutputData(fallbackOutputData);
    }

    if (!fallbackOutputData && !rawDataFromNode && rawOutputData) {
      setRawOutputData(null);
    }
  }, [
    (data as any).rawOutputData,
    (data as any).executionResult,
    (data as any).outputData,
    hasLogsDeep,
    rawOutputData,
  ]);

  // Execute Python code
  const runCode = async () => {
    if (!code.trim()) {
      toast({
        variant: "destructive",
        title: "No Code",
        description: "Please write some Python code to execute",
      });
      return;
    }

    if (!code.includes("result")) {
      toast({
        variant: "destructive",
        title: "Missing Output",
        description:
          "Your code must assign output to 'result'. Example: result = {'key': 'value'}",
        duration: 4000,
      });
      return;
    }

    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Title is required for the Python code node",
        duration: 3000,
      });
      return;
    }

    // Get plugin ID from session
    const pluginId = sessionStorage.getItem("editPluginId");

    if (!pluginId || pluginId === "temp-plugin") {
      toast({
        variant: "destructive",
        title: "Plugin Not Saved",
        description: "Please save the plugin first before testing",
        duration: 3000,
      });
      return;
    }

    // Get active mesh flow from sessionStorage
    const activeMeshFlowStr = sessionStorage.getItem("activeMeshFlow");
    if (!activeMeshFlowStr) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "No active mesh flow. Please create or select a mesh flow first.",
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    setOutputData(null);
    setErrorResponse(null);

    try {
      const activeMeshFlow = JSON.parse(activeMeshFlowStr);

      const currentNode = reactFlowInstance?.getNode(id);
      if (!currentNode) {
        throw new Error("Current node not found");
      }

      const allNodes = reactFlowInstance.getNodes();
      const allEdges = reactFlowInstance.getEdges();
      const executableNodes = getExecutionPath(id, allNodes, allEdges);

      // Mark all executable nodes as pending so loading border is visible.
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          const isExecutable = executableNodes.some(
            (n: any) => n.id === node.id,
          );
          if (!isExecutable) return node;

          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: "pending",
              executionResult: undefined,
              error: undefined,
            },
          };
        }),
      );

      // Build nodes array for workflow
      const workflowNodes = executableNodes.map((node) => {
        let nodeType = "core";
        let subType = "http_request";
        let nodeParameters;
        let parameters;

        if (node.id === id) {
          // This is the current PythonCodeNode - build from local state
          nodeParameters = {
            code,
            mode,
            continueOnFail,
            options: {
              python_env: pythonEnv,
            },
          };

          nodeType = "code_node";
          subType = "python_code";
          parameters = {
            ...nodeParameters,
            label: label,
          };
        } else {
          // Other nodes - use centralized utility (checks storedParameters first)
          const { nodeParameters: np, parameters: p } =
            getNodeExecutionParameters(node, pluginId);
          nodeParameters = np;
          parameters = p;

          const backendType = getNodeBackendType(node.type || "custom");
          nodeType = backendType.type;
          subType = backendType.sub_type;
        }

        return {
          uid: node.data.uid || node.id,
          title: node.data.title || node.data.label || "Untitled",
          description: node.data.description || "",
          type: nodeType,
          sub_type: subType,
          version: "1.0",
          position: [node.position.x, node.position.y],
          parameters,
        };
      });

      // Create mapping from React Flow node IDs to UIDs
      const nodeIdToUidMap: Record<string, string> = {};
      executableNodes.forEach((node) => {
        const nodeUid = node.data.uid || node.id;
        nodeIdToUidMap[node.id] = nodeUid;
      });

      // Create Set of node IDs in execution path for filtering
      const executionPathNodeIds = new Set(executableNodes.map((n) => n.id));

      // Build connections between nodes
      const connections: any = {};
      allEdges
        .filter(
          (edge) =>
            executionPathNodeIds.has(edge.source) &&
            executionPathNodeIds.has(edge.target),
        )
        .forEach((edge) => {
          const sourceUid = nodeIdToUidMap[edge.source];
          const targetUid = nodeIdToUidMap[edge.target];

          if (!sourceUid || !targetUid) return;

          if (!connections[sourceUid]) {
            connections[sourceUid] = {
              main: [],
            };
          }

          // Check if source node is a switch node
          const sourceNode = executableNodes.find((n) => n.id === edge.source);
          const isSwitchNode =
            sourceNode?.type === "switchNode" ||
            sourceNode?.data?.sub_type === "switch_case";

          // Check if source node is an if_else node
          const isIfElseNode =
            sourceNode?.type === "ifElseNode" ||
            sourceNode?.data?.sub_type === "if_else";

          // Determine output index based on node type and handle
          let outputIndex = 0;

          if (isSwitchNode && edge.sourceHandle) {
            // For switch nodes: output_0 → main[0], output_1 → main[1], etc.
            const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
            if (handleMatch) {
              outputIndex = parseInt(handleMatch[1], 10);
            }
          } else if (isIfElseNode && edge.sourceHandle) {
            // For if_else nodes: true handle → main[0], false handle → main[1]
            outputIndex = edge.sourceHandle === "false" ? 1 : 0;
          }

          if (!connections[sourceUid].main[outputIndex]) {
            connections[sourceUid].main[outputIndex] = [];
          }

          connections[sourceUid].main[outputIndex].push({
            node: targetUid,
            type: "main",
            index: 0,
          });
        });

      // Build complete workflow payload using centralized utility
      const workflowPayload = buildWorkflowPayload(
        activeMeshFlow,
        workflowNodes,
        connections,
      );

      const response = await executeWorkflow(workflowPayload);

      // Update ALL executed nodes with their results (not just the clicked node)
      if (response?.results?.[0]?.node_results) {
        const nodeResults = response.results[0].node_results;

        // Use the utility function to handle node execution results
        const { hasErrors, errorMessage, errorNodeTitle } =
          handleNodeExecutionResults(reactFlowInstance, nodeResults, allEdges);

        // Extract this node's output for local state
        const resultsMap = new Map<string, any>();
        nodeResults.forEach((nodeResult: any) => {
          resultsMap.set(nodeResult.uid, nodeResult);
        });

        const currentNodeUidCandidates = Array.from(
          new Set([
            uid,
            id,
            currentNode?.data?.uid,
            currentNode?.id,
            data?.uid,
          ]),
        ).filter(Boolean);

        const currentNodeResult =
          nodeResults.find((nodeResult: any) =>
            currentNodeUidCandidates.includes(nodeResult.uid),
          ) || resultsMap.get(uid || id);

        if (currentNodeResult) {
          // Check if this node failed
          const isFailed =
            currentNodeResult.status === "error" ||
            currentNodeResult.status === "failed" ||
            currentNodeResult.status === "failure";

          if (isFailed) {
            // Show error in ResponseViewer but don't pass to downstream nodes
            setErrorResponse(currentNodeResult.error || "Execution failed");
            setOutputData(null);
            setRawOutputData(currentNodeResult);
          } else if (currentNodeResult.output_data) {
            // Success - extract and display output data
            const rawResponse = currentNodeResult?.output_data;

            // Keep full node result for console logs while preserving output display from output_data.
            setRawOutputData(currentNodeResult);
            setOutputData(unwrapJsonData(rawResponse));
            setErrorResponse(null);
          } else {
            // Some backend responses attach logs at node-result level without output_data.
            setRawOutputData(currentNodeResult);
          }
        }

        // Show appropriate toast based on execution result
        if (hasErrors) {
          // Show error toast with specific error message
          const errorDescription = errorMessage
            ? `${errorNodeTitle}: ${errorMessage}`
            : "One or more nodes failed during execution";

          const shortError =
            errorDescription.length > 150
              ? errorDescription.substring(0, 150) + "..."
              : errorDescription;

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description: shortError,
            duration: 7000,
          });
        } else {
          // Only show success if ALL nodes succeeded
          toast({
            variant: "success",
            title: "Success",
            description: "Python Code executed successfully",
            duration: 3000,
          });
        }
      } else {
        // Fallback - no node results in response
        // Check for success at top level
        const isSuccess =
          response?.success === true ||
          response?.status === "success" ||
          response?.body?.status === 1 ||
          response?.body?.status === "1" ||
          response?.status === 1;

        if (isSuccess) {
          // Mark current node as success
          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "success",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "success",
            title: "Success",
            description:
              response?.message || "Python Code executed successfully",
            duration: 3000,
          });
        } else {
          // Mark current node and all downstream nodes as failed
          const allEdges = reactFlowInstance.getEdges();
          const downstreamNodes = findDownstreamNodes(id, allEdges);

          reactFlowInstance.setNodes((nodes) =>
            nodes.map((node) => {
              if (node.id === id) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error:
                      response?.body?.message ||
                      response?.message ||
                      "Execution failed",
                  },
                };
              } else if (downstreamNodes.has(node.id)) {
                return {
                  ...node,
                  data: {
                    ...node.data,
                    executionStatus: "failed",
                    error: "Not executed due to upstream failure",
                  },
                };
              }
              return node;
            }),
          );

          toast({
            variant: "destructive",
            title: "Execution Failed",
            description:
              response?.body?.message ||
              response?.message ||
              "Python Code execution failed",
            duration: 4000,
          });
        }
      }
    } catch (error: any) {
      console.error("Python Code Execution Error:", error);

      // Set error response for display
      setErrorResponse({
        error: error?.message || "Python Code execution failed",
        details:
          error?.response?.data ||
          error?.stack ||
          "No additional details available",
      });
      setOutputData(null);

      // Get all edges for finding downstream nodes
      const allEdges = reactFlowInstance.getEdges();
      const downstreamNodes = findDownstreamNodes(id, allEdges);

      // Update current node and all downstream nodes to failed status
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            // Mark the failed node as failed
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: error?.message || "Python Code execution failed",
              },
            };
          } else if (downstreamNodes.has(node.id)) {
            // Mark downstream nodes as failed (not executed due to upstream failure)
            return {
              ...node,
              data: {
                ...node.data,
                executionStatus: "failed",
                error: "Not executed due to upstream failure",
              },
            };
          }
          return node;
        }),
      );

      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || "Python Code execution failed",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Enhanced Python linter - checks for common syntax issues
  const pythonLinter = linter((view) => {
    const diagnostics: Diagnostic[] = [];
    const code = view.state.doc.toString();
    const lines = code.split("\n");

    lines.forEach((line, index) => {
      const lineObj = view.state.doc.line(index + 1);
      const trimmedLine = line.trim();

      // Skip empty lines and comments
      if (!trimmedLine || trimmedLine.startsWith("#")) return;

      // 1. Check for unclosed strings (improved)
      let inString = false;
      let stringChar = "";
      let escaped = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];

        if (escaped) {
          escaped = false;
          continue;
        }

        if (char === "\\") {
          escaped = true;
          continue;
        }

        if ((char === '"' || char === "'") && !inString) {
          inString = true;
          stringChar = char;
        } else if (char === stringChar && inString) {
          inString = false;
        }
      }

      if (inString) {
        diagnostics.push({
          from: lineObj.from,
          to: lineObj.to,
          severity: "error",
          message: `Unterminated string literal`,
        });
      }

      // 2. Missing colon after control structures
      const controlStructures =
        /^\s*(if|elif|else|for|while|def|class|try|except|finally|with|async\s+def|async\s+with|async\s+for)\s+/;
      if (controlStructures.test(line) && !line.trim().endsWith(":")) {
        // Check if it's not just 'else:' or 'finally:' which are correct
        if (!line.match(/^\s*(else|finally|try)\s*$/)) {
          diagnostics.push({
            from: lineObj.from,
            to: lineObj.to,
            severity: "error",
            message: "Expected ':' at end of line",
          });
        }
      }

      // 3. Common typos
      const typos = {
        printt: "print",
        retrun: "return",
        imoprt: "import",
        improt: "import",
        form: "from",
        dfe: "def",
        slef: "self",
        esle: "else",
        fro: "for",
        whiel: "while",
      };

      Object.entries(typos).forEach(([typo, correct]) => {
        const regex = new RegExp(`\\b${typo}\\b`);
        const match = line.match(regex);
        if (match && match.index !== undefined) {
          diagnostics.push({
            from: lineObj.from + match.index,
            to: lineObj.from + match.index + typo.length,
            severity: "error",
            message: `Undefined name '${typo}'. Did you mean '${correct}'?`,
          });
        }
      });

      // 4. Mixed tabs and spaces
      if (line.match(/^\t/) && code.match(/^ /m)) {
        diagnostics.push({
          from: lineObj.from,
          to: lineObj.from + 1,
          severity: "warning",
          message: "Inconsistent use of tabs and spaces in indentation",
        });
      }

      // 5. Invalid syntax patterns
      if (line.match(/\)\s*\(/)) {
        diagnostics.push({
          from: lineObj.from,
          to: lineObj.to,
          severity: "warning",
          message: "Missing operator between expressions",
        });
      }

      // 6. Unmatched brackets — checked across the WHOLE document, not per line.
      // This prevents false positives while the user is mid-way through a
      // multi-line structure like:  result = [\n  {\n    "key": "value"\n  }\n]
      const totalOpen = (code.match(/\(/g) || []).length;
      const totalClose = (code.match(/\)/g) || []).length;
      const totalOpenB = (code.match(/\[/g) || []).length;
      const totalCloseB = (code.match(/\]/g) || []).length;
      const totalOpenBr = (code.match(/\{/g) || []).length;
      const totalCloseBr = (code.match(/\}/g) || []).length;

      // Only flag when the document has content worth validating (> 3 non-empty lines)
      // AND the imbalance is "closing exceeds opening" — that's always wrong.
      // Opening exceeds closing is normal while typing a multi-line structure.
      const nonEmptyLineCount = lines.filter((l) => l.trim()).length;
      if (nonEmptyLineCount > 3) {
        if (totalClose > totalOpen) {
          const lastLine = view.state.doc.line(view.state.doc.lines);
          diagnostics.push({
            from: lastLine.from,
            to: lastLine.to,
            severity: "error",
            message: `Unmatched parentheses — ${totalClose - totalOpen} extra closing ')'`,
          });
        }
        if (totalCloseB > totalOpenB) {
          const lastLine = view.state.doc.line(view.state.doc.lines);
          diagnostics.push({
            from: lastLine.from,
            to: lastLine.to,
            severity: "error",
            message: `Unmatched brackets — ${totalCloseB - totalOpenB} extra closing ']'`,
          });
        }
        if (totalCloseBr > totalOpenBr) {
          const lastLine = view.state.doc.line(view.state.doc.lines);
          diagnostics.push({
            from: lastLine.from,
            to: lastLine.to,
            severity: "error",
            message: `Unmatched braces — ${totalCloseBr - totalOpenBr} extra closing '}'`,
          });
        }
      }

      // 7. Invalid indentation (not multiple of 4)
      const leadingSpaces = line.match(/^ */)?.[0].length || 0;
      if (leadingSpaces > 0 && leadingSpaces % 4 !== 0 && !line.match(/^\t/)) {
        diagnostics.push({
          from: lineObj.from,
          to: lineObj.from + leadingSpaces,
          severity: "warning",
          message: "Indentation should be a multiple of 4 spaces",
        });
      }

      // 8. Assignment in conditional (common mistake)
      if (line.match(/^\s*if\s+.*[^=!<>]=[^=]/)) {
        diagnostics.push({
          from: lineObj.from,
          to: lineObj.to,
          severity: "warning",
          message: "Assignment in condition. Did you mean '==' for comparison?",
        });
      }

      // 9. Multiple statements on one line
      if (line.includes(";") && !line.trim().startsWith("#")) {
        const semiColonIndex = line.indexOf(";");
        diagnostics.push({
          from: lineObj.from + semiColonIndex,
          to: lineObj.from + semiColonIndex + 1,
          severity: "warning",
          message: "Multiple statements on one line (not recommended)",
        });
      }

      // 10. Invalid variable names with Python keywords
      const varDeclaration = line.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*=/);
      if (varDeclaration) {
        const varName = varDeclaration[1];

        // Check for Python keywords
        const keywords = [
          "and",
          "as",
          "assert",
          "break",
          "class",
          "continue",
          "def",
          "del",
          "elif",
          "else",
          "except",
          "False",
          "finally",
          "for",
          "from",
          "global",
          "if",
          "import",
          "in",
          "is",
          "lambda",
          "None",
          "nonlocal",
          "not",
          "or",
          "pass",
          "raise",
          "return",
          "True",
          "try",
          "while",
          "with",
          "yield",
        ];

        if (keywords.includes(varName)) {
          const startPos = line.indexOf(varName);
          diagnostics.push({
            from: lineObj.from + startPos,
            to: lineObj.from + startPos + varName.length,
            severity: "error",
            message: `Cannot assign to keyword '${varName}'`,
          });
        }
      }

      // 11. Check for common function call typos
      if (line.match(/\blne\b/)) {
        const match = line.match(/\blne\b/);
        if (match && match.index !== undefined) {
          diagnostics.push({
            from: lineObj.from + match.index,
            to: lineObj.from + match.index + 3,
            severity: "error",
            message: "Undefined name 'lne'. Did you mean 'len'?",
          });
        }
      }

      // 12. else/elif without if
      if (line.match(/^\s*(elif|else)\s+/) || line.match(/^\s*else\s*:/)) {
        // Check if there's an 'if' statement before this
        const previousNonEmptyLines = lines
          .slice(0, index)
          .filter((l) => l.trim());
        const hasIf = previousNonEmptyLines.some(
          (l) => l.match(/^\s*if\s+/) || l.match(/^\s*elif\s+/),
        );

        if (!hasIf && index > 0) {
          diagnostics.push({
            from: lineObj.from,
            to: lineObj.to,
            severity: "error",
            message: `'${trimmedLine.split(/\s+/)[0]}' without matching 'if'`,
          });
        }
      }
    });

    const hasResult = lines.some((line) => {
      const trimmed = line.trim();
      return (
        trimmed.startsWith("result") &&
        trimmed.includes("=") &&
        !trimmed.startsWith("#")
      );
    });
    const meaningfulLineCount = lines.filter(
      (l) => l.trim() && !l.trim().startsWith("#"),
    ).length;

    if (!hasResult && meaningfulLineCount > 2) {
      if (!hasResult && meaningfulLineCount > 2) {
        const lastNonEmptyIndex = [...lines]
          .reverse()
          .findIndex((l) => l.trim().length > 0);
        const lastLineIndex =
          lastNonEmptyIndex >= 0 ? lines.length - 1 - lastNonEmptyIndex : 0;
        const lastLineObj = view.state.doc.line(lastLineIndex + 1);

        diagnostics.push({
          from: lastLineObj.from,
          to: lastLineObj.to,
          severity: "warning",
          message:
            "Missing 'result' assignment. Your code must assign output to 'result'. Example: result = {'key': 'value'}",
        });
      }
    }
    return diagnostics;
  });

  // Delete handler
  const handleDelete = (nodeId: string) => {
    setDeleteId(nodeId);
    setShowDeleteConfirm(true);
  };

  // Handle delete confirmation
  const confirmDelete = () => {
    if (deleteId && data.onDelete) {
      data.onDelete(deleteId);
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
  };

  // Helper to get display data from connected nodes
  const getDisplayData = (input: any) => {
    const EXCLUDE = [
      "uid",
      "isDecript",
      "isDecrypted",
      "title",
      "testConnectionBtn",
      "tabId",
      "configType",
      "onDelete",
      "onDataChange",
      "header",
      "borderColor",
      "shadowColor",
      "label",
      "nodeType",
      "itemCount",
    ];

    // Extract json_data from backend output format
    const extractJsonData = (data: any) => {
      if (Array.isArray(data)) {
        return data.map((item) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        });
      }
      if (data && typeof data === "object" && "json_data" in data) {
        return data.json_data;
      }
      return data;
    };

    // Priority: response > outputData > configData
    if (input.response) {
      return extractJsonData(input.response);
    }

    if (input.outputData) {
      return extractJsonData(input.outputData);
    }

    // For configData, create an object
    const displayData: any = {};
    if (input.configData) {
      const encryptedFields = input.configData.isDecript || [];

      Object.entries(input.configData)
        .filter(([key]) => !EXCLUDE.includes(key))
        .forEach(([key, value]) => {
          if (Array.isArray(encryptedFields) && encryptedFields.includes(key)) {
            displayData[key] = "********************";
          } else {
            displayData[key] = value;
          }
        });
    }
    return displayData;
  };

  // Render input data section
  const renderInputData = () => {
    if (!inputData?.length) {
      return (
        <div className="text-xs text-muted-foreground p-4 border rounded-md bg-muted/20 text-center">
          <div className="font-medium">No Input Connected</div>
          <div>Connect a node to see data here</div>
        </div>
      );
    }

    // Build sources array for PaginatedInputViewer (handles tabs internally)
    const sources = inputData.map((input, index) => {
      // Generate label: if from switch node, use "Rule X", otherwise use original label
      let displayLabel = input.label || `Source ${index + 1}`;

      // Check if label indicates switch node (e.g., "switch", "SWITCH")
      if (displayLabel.toLowerCase().includes("switch")) {
        displayLabel = `Rule ${index + 1}`;
      }

      return {
        label: displayLabel,
        data: getDisplayData(input),
        isConfig: !!input.configData, // Mark as config if source is configuration
      };
    });
    return (
      <PaginatedInputViewer
        sources={sources}
        onRefresh={refreshInputData}
        sourceCount={inputData.length}
      />
    );
  };

  return (
    <FullscreenWrapper
      id={id}
      isExpanded={isExpanded}
      executionStatus={(data as any).executionStatus}
      borderColor={data.borderColor}
      shadowColor={data.shadowColor}
      onContextMenu={handleContextMenu}
      expandedClassName="w-[90vw] !max-w-[1000px] flex flex-col"
    >
      <Confirmation
        text="Are you sure you want to delete this node?"
        confirmAction={confirmDelete}
        rejectAction={() => setShowDeleteConfirm(false)}
        confirmText="Confirm"
        rejectText="Cancel"
        show={showDeleteConfirm}
        closeDialog={() => setShowDeleteConfirm(false)}
      />

      {/* Context Menu */}
      <NodeContextMenu
        show={showContextMenu}
        position={contextMenuPosition}
        onClose={closeContextMenu}
        items={[
          createContextMenuItems.expandCollapse(isExpanded, () =>
            setIsExpanded((prev) => !prev),
          ),
          createContextMenuItems.delete(
            () => handleDelete(id),
            !!data.onDelete,
          ),
        ]}
      />

      {/* Handles */}
      <Handle type="target" position={Position.Left} className={handlerStyle} />
      <Handle
        type="source"
        position={Position.Right}
        className={handlerStyle}
      />

      {/* Header */}
      <div
        className={`flex items-center justify-between gap-4 rounded-md transition-all duration-500 flex-shrink-0 ${
          isExpanded ? "p-3" : "!p-3"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Code
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce-horizontal"
                  : ""
              }
            />
          }
          label={label}
          title={title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          description={description}
        />
        <FullscreenToggleButton isExpanded={isExpanded} />
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {/* Title & Description */}
          <div className="mt-2 p-4 border-t flex items-start gap-4 flex-shrink-0">
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Title <span className="text-primary">*</span>
              </label>
              <ValidatedTitleInput
                id={id}
                title={title}
                onChange={setTitle}
                nodes={nodes}
                onValidationChange={setIsTitleValid}
              />
            </div>
            <div className="space-y-1 w-1/2">
              <label className="text-xs font-medium text-muted-foreground">
                Node Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter node description"
                className="w-full p-2 border rounded text-xs resize-none text-muted-foreground bg-background"
                rows={1}
              />
            </div>
          </div>

          {/* 3 Section Grid */}
          <div
            ref={containerRef}
            className="flex gap-0 p-4 border-t flex-1 min-h-0 overflow-hidden"
          >
            {/* Left Section - Input Data (4/12 columns) */}
            <div
              style={{ width: `${widths[0]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection scrollable={false} hasRelative={true}>
                {renderInputData()}
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(0)} />

            {/* Middle Section - Python Code Parameters (4/12 columns) */}
            <div
              style={{ width: `${widths[1]}%`, minWidth: 80, flexShrink: 0 }}
            >
              <NodeContentSection hasRelative={true} scrollable={false}>
                <div className="flex-1 min-h-0 flex flex-col">
                  {/* Single scroll surface — wraps all scrollable content */}
                  <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col gap-2 relative">
                    <h3 className="text-sm font-semibold sticky top-0 bg-background z-10 pb-2 border-b">
                      Parameters
                    </h3>

                    {/* Mode Selection */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold">
                        Execution Mode
                      </label>
                      <Select
                        value={mode}
                        onValueChange={(val: any) => setMode(val)}
                      >
                        <SelectTrigger className="text-xs h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem
                              value="runOnceForAllItems"
                              className="text-xs"
                            >
                              Run Once for All Items
                            </SelectItem>
                            <SelectItem
                              value="runOnceForEachItem"
                              className="text-xs"
                            >
                              Run Once for Each Item
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Python Code Editor */}
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-semibold">
                          Python Code
                        </label>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowHintsDrawer(!showHintsDrawer)}
                            className={`h-6 w-6 p-0 rounded ${
                              showHintsDrawer
                                ? "bg-muted text-primary-foreground"
                                : "bg-muted hover:bg-lightGray"
                            }`}
                            title="Toggle variable hints"
                          >
                            {showHintsDrawer ? (
                              <CircleX className="text-primary" size={14} />
                            ) : (
                              <Info
                                style={{ color: "var(--primary-amber)" }}
                                size={14}
                              />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsCodeEditorExpanded(true)}
                            className="h-6 w-6 p-0 bg-muted hover:bg-lightGray rounded"
                            title="Expand editor"
                          >
                            <Maximize2 size={14} className="text-primary" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div
                          className={`border rounded-md overflow-hidden transition-all duration-300 ${
                            showHintsDrawer ? "flex-1" : "w-full"
                          }`}
                        >
                          <CodeMirror
                            value={code}
                            height="295px"
                            extensions={[
                              tooltipPortalExtension,
                              python(),
                              pythonLinter,
                              meshflowExtension,
                            ]}
                            onChange={(value) => setCode(value)}
                            theme="light"
                            className="text-sm"
                            placeholder={"# write your python code here"}
                          />
                        </div>
                        {showHintsDrawer && (
                          <div className="w-64 border rounded-md bg-background overflow-hidden flex flex-col">
                            <div className="bg-muted px-3 py-2 border-b sticky top-0">
                              <h4 className="text-xs font-semibold text-foreground">
                                Hints
                              </h4>
                            </div>
                            <div className="overflow-y-auto flex-1 p-2 space-y-2 max-h-[295px]">
                              {MESH_HINTS.map((hint, index) => (
                                <div
                                  key={index}
                                  className="p-2 border rounded-md bg-muted/30 hover:bg-muted/50 transition cursor-pointer"
                                  onClick={() => {
                                    // Insert the hint into the editor at cursor position
                                    const insertText = hint.apply || hint.label;
                                    setCode((prev) => prev + insertText);
                                  }}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <code className="text-xs font-semibold text-foreground">
                                      {hint.label}
                                    </code>
                                    <span
                                      className="text-[9px] px-1.5 py-0.5 rounded"
                                      style={{
                                        backgroundColor: hint.color + "20",
                                        color: hint.color,
                                      }}
                                    >
                                      {hint.type}
                                    </span>
                                  </div>
                                  <div
                                    className="text-[10px] mb-1.5 uppercase font-semibold tracking-wide"
                                    style={{ color: hint.color }}
                                  >
                                    {hint.category}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground mb-1.5">
                                    {hint.desc}
                                  </div>
                                  <code className="text-[9px] block bg-muted px-2 py-1 rounded text-foreground/80">
                                    {hint.example}
                                  </code>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-2 pt-2 border-t">
                      <h4 className="text-xs font-semibold">Options</h4>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={continueOnFail}
                          onCheckedChange={setContinueOnFail}
                          className="scale-75"
                        />
                        <span className="text-xs font-medium">
                          Continue on Fail
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Execute Button */}
                  <div className="flex-shrink-0 pt-2 border-t bg-background">
                    <Button
                      onClick={runCode}
                      disabled={
                        !inputData ||
                        !isTitleValid ||
                        inputData.length === 0 ||
                        !code.trim() ||
                        loading
                      }
                      className="w-full sticky bottom-0 left-0 right-0 rounded-b-md"
                      size="sm"
                    >
                      <FlaskConical size={14} className="mr-2" />
                      Execute step
                    </Button>
                  </div>
                </div>
              </NodeContentSection>
            </div>

            <PanelDivider onMouseDown={startDrag(1)} />

            {/* Right Section - Response (4/12 columns) */}
            <div style={{ flex: 1, minWidth: 80 }}>
              <NodeContentSection scrollable={false} hasRelative={true}>
                <ResponseViewer
                  response={errorResponse || outputData}
                  loading={loading}
                  emptyMessage="Execute the Python code to see output"
                  loadingMessage="Executing Python code..."
                  onCopy={handleCopyOutput}
                  isError={!!errorResponse}
                  isLoopChild={(data as any).is_loop_child || false}
                  totalIterations={(data as any).total_iterations || 0}
                  iterations={(data as any).iterations || []}
                  nodeType="pythonCodeNode"
                  rawOutputData={rawOutputData}
                  fillParent={true}
                  onResponseChange={(newData) => {
                    setOutputData(newData);
                    data.onDataChange?.(id, {
                      response: newData,
                      manuallyEdited: true,
                    });
                  }}
                />
              </NodeContentSection>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Code Editor Dialog */}
      <Dialog
        open={isCodeEditorExpanded}
        onOpenChange={setIsCodeEditorExpanded}
      >
        <DialogContent className="max-w-[60vw] max-h-[90vh] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between mt-3">
              <span>Python Code</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setEditorTheme(editorTheme === "light" ? "dark" : "light")
                  }
                  className="h-8 w-8 p-0 bg-muted hover:bg-lightGray rounded"
                  title={`Switch to ${editorTheme === "light" ? "dark" : "light"} theme`}
                >
                  {editorTheme === "light" ? (
                    <Moon size={16} />
                  ) : (
                    <Sun size={16} />
                  )}
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            <div className="border rounded-md overflow-hidden flex-1">
              <CodeMirror
                value={code}
                height="100%"
                extensions={[
                  tooltipPortalExtension,
                  python(),
                  pythonLinter,
                  meshflowExtension,
                ]}
                onChange={(value) => setCode(value)}
                theme={editorTheme === "light" ? "light" : oneDark}
                className="text-sm h-full"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </FullscreenWrapper>
  );
};

export default PythonCodeNode;
